import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Loader2, Crown } from 'lucide-react';
import { fetchPremiumFlags, savePremiumFlags } from '@/lib/studyModes';

/**
 * Premium kill switches (Phase 00 guardrail). Each Premium module has its own
 * independent switch plus a master switch: turning one off makes its routes 404
 * and hides it from students within ~20 seconds — the existing experience keeps
 * working exactly as before.
 */
export function PremiumFlagsPanel() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPremiumFlags().then(setState).catch(() => setState(null)); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const payload = {
        premium_enabled: next.premium_enabled,
        modules: Object.fromEntries(next.modules.map(m => [m.key, m.switch_value])),
      };
      setState(await savePremiumFlags(payload));
      toast.success('Premium switches updated — live within 20 seconds');
    } catch {
      toast.error('Could not update the Premium switches');
    }
    setSaving(false);
  };

  if (!state) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="premium-flags-panel">
        <p className="flex items-center gap-2 text-sm text-slate-400 font-body"><Loader2 className="w-4 h-4 animate-spin" /> Loading Premium switches…</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border-2 border-fuchsia-100 p-5 space-y-4" data-testid="premium-flags-panel">
      <div className="flex items-start gap-2.5">
        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500 to-rose-500 text-white flex items-center justify-center flex-shrink-0">
          <Crown className="w-4.5 h-4.5" />
        </span>
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Premium modules &amp; kill switches</h3>
          <p className="text-xs text-slate-500 font-body">
            Premium is an additive layer. Switch anything off and it disappears for students within 20 seconds — chat, quizzes, flashcards, gallery and every admin screen keep working exactly as before.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between p-3 rounded-xl border-2 border-slate-200 bg-slate-50">
        <div>
          <p className="font-heading text-sm text-slate-800 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Premium master switch
          </p>
          <p className="text-[11px] text-slate-500 font-body">Off = the whole Premium layer goes dark (pre-Premium experience for everyone).</p>
        </div>
        <Switch data-testid="premium-master-switch" disabled={saving}
          checked={state.premium_enabled}
          onCheckedChange={(v) => save({ ...state, premium_enabled: v })}
          className="data-[state=checked]:bg-fuchsia-500" />
      </div>

      <div className="space-y-2">
        {state.modules.map(m => (
          <div key={m.key} className={`flex items-center justify-between p-3 rounded-xl border ${m.enabled ? 'border-emerald-100 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
            <div className="min-w-0">
              <p className="font-heading text-sm text-slate-800 truncate">{m.label}</p>
              <p className="text-[11px] text-slate-400 font-body">
                {m.enabled ? 'Live for Premium & Elite (and trial students)' : state.premium_enabled ? 'Dark-launched — hidden from everyone' : 'Off via the master switch'}
              </p>
            </div>
            <Switch data-testid={`premium-module-switch-${m.key}`} disabled={saving}
              checked={m.switch_value}
              onCheckedChange={(v) => save({
                ...state,
                modules: state.modules.map(x => (x.key === m.key ? { ...x, switch_value: v } : x)),
              })}
              className="data-[state=checked]:bg-emerald-500" />
          </div>
        ))}
      </div>

      <p className="text-[11px] text-slate-400 font-body">
        Rollback drill: flip a switch off, reload a student session — the mode picker, study sidebar and guided sessions vanish and every Premium API answers 404.
      </p>
      <Button size="sm" variant="outline" data-testid="premium-flags-refresh" disabled={saving}
        onClick={() => fetchPremiumFlags().then(setState)}>
        Refresh
      </Button>
    </div>
  );
}

export default PremiumFlagsPanel;
